import { InputGroup, Text } from "@smartsuite/react-ui/lib";
import cx from "classnames";
import React, { useMemo } from "react";
import { NumericFormat } from "react-number-format";
import { FormFieldControl } from "../../../../components/FormFieldControl/FormFieldControl";
import { useFormField } from "../../../../hooks/useFormField";
import { FormFieldProps } from "../../../types";
import { TextFieldValue } from "../../text/text.config";
import { validateByType } from "../../text/validation";
import { PlainTextFieldProps } from "@smartsuite/react-ui/lib/components/forms/PlainTextField/PlainTextField";
import { MAX_PRECISION } from "@smartsuite/types";

import "./PercentFieldControl.sass";
import { getSeparators, isAllowedValue } from "../../number/NumberFieldControl/utils";

export type PercentFieldControlProps = FormFieldProps &
  Pick<PlainTextFieldProps, "placeholder" | "type">;

export const PercentFieldControl: React.FunctionComponent<PercentFieldControlProps> = ({
  caption,
  className,
  field,
  formItem,
  label,
  name,
  placeholder,
  required,
  type,
  readOnly,
}) => {
  const formField = useFormField<TextFieldValue>({
    name,
    validate: validateByType(type, field, formItem),
  });

  const inputClassName = cx("form-percent-field", className, {
    "has-value": !!formField.value,
  });

  const separators = getSeparators(field.params);

  const fieldParameters = useMemo(
    () => ({
      max_length: field.params.max_length,
      allow_negative: field.params.allow_negative,
      decimal_separator: separators.decimal_separator,
      thousand_separator: separators.thousand_separator,
    }),
    [
      field.params.allow_negative,
      field.params.max_length,
      separators.decimal_separator,
      separators.thousand_separator,
    ]
  );

  return (
    <FormFieldControl
      captionAsTooltip={formItem.params.help_text_as_tooltip}
      caption={caption}
      helpText={formItem.params.helpText}
      errorMessage={formField.errorMessage}
      label={label}
      required={required}
      state={formField.state}
      readOnly={readOnly}
    >
      <InputGroup
        className="form-percent-field-group"
        inputBackground="white-new"
        state={formField.state}
      >
        <NumericFormat
          className={inputClassName}
          value={formField.value}
          onBlur={formField.onBlur}
          onValueChange={(values) => {
            formField.onChange(values.floatValue ?? "");
          }}
          onFocus={formField.onFocus}
          placeholder={placeholder}
          readOnly={readOnly}
          required={required}
          decimalScale={MAX_PRECISION}
          decimalSeparator={fieldParameters.decimal_separator}
          thousandSeparator={fieldParameters.thousand_separator}
          isAllowed={({ formattedValue }) => isAllowedValue(formattedValue, fieldParameters)}
          name={formField.name}
          allowNegative={fieldParameters.allow_negative}
        />

        <Text size="l" color="secondary" className="form-percent-field__legend">
          %
        </Text>
      </InputGroup>
    </FormFieldControl>
  );
};
